import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import {
  Eye,
  EyeOff,
  Info,
  KeyRound,
  Lock,
  Shield,
  ShieldCheck,
  ArrowLeft,
} from 'lucide-react';
import { getApiEndpoint } from '@/utils/ERPUtils';
import { useAuth } from '@/context/AuthContext';
import site_logo from '@/assets/site_logo.jpg';
import { BRAND_NAME, getCachedBrandLogo, setCachedBrandLogo } from '@/utils/branding';
import SiteFooter from '@/components/common/SiteFooter';
import { logger } from '@/utils/logger';

// Client-side strength hint only - the server enforces the actual password
// policy. Mirrors the design's Empty / Weak / Fair / Strong indicator.
const passwordStrength = (
  pw: string,
): { label: string; className: string; fill: string } => {
  if (!pw) {
    return { label: 'Empty', className: 'text-destructive', fill: 'w-0' };
  }
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
  if (score <= 1) {
    return { label: 'Weak', className: 'text-destructive', fill: 'w-1/4 bg-destructive' };
  }
  if (score === 2) {
    return { label: 'Fair', className: 'text-amber-500', fill: 'w-2/4 bg-amber-500' };
  }
  if (score === 3) {
    return { label: 'Good', className: 'text-primary', fill: 'w-3/4 bg-primary' };
  }
  return {
    label: 'Strong',
    className: 'text-[hsl(var(--success))]',
    fill: 'w-full bg-[hsl(var(--success))]',
  };
};

const FEATURE_CARDS = [
  {
    icon: ShieldCheck,
    title: 'Defense in Depth',
    text: 'Layered credential protection - encrypted transport, irreversible one-way storage.',
  },
  {
    icon: KeyRound,
    title: 'Secure Auth',
    text: 'Multi-factor authentication ready.',
  },
];

const ResetPassword: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [brandLogo, setBrandLogo] = useState<string | null>(getCachedBrandLogo);
  const [brandName, setBrandName] = useState<string>(BRAND_NAME);
  const [logoLoaded, setLogoLoaded] = useState(false);

  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const hasNavigatedRef = React.useRef(false);

  // Redirect if already logged in (prevent double navigation)
  React.useEffect(() => {
    if (user && !hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  // Fetch company branding (logo + name) for pre-auth display - same
  // pattern as the login page. Falls back to the bundled site_logo.
  useEffect(() => {
    let mounted = true;
    axios
      .get(getApiEndpoint('GET_DEFAULT_BRANDING'))
      .then((res) => {
        if (!mounted) return;
        const data = res?.data?.message?.data;
        // Fall back to the bundled logo when no company logo is set.
        const url = data?.logo_url || null;
        setBrandLogo(url);
        setCachedBrandLogo(url); // keep the reload cache in sync
        if (data?.company_name) setBrandName(data.company_name);
      })
      .catch((err) => {
        logger.error('Branding fetch failed:', err);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const queryParams = new URLSearchParams(location.search);
  const resetKey = queryParams.get('key');

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!password.trim() || !confirmPassword.trim()) {
      toast({
        title: "Validation Error",
        description: "Please enter and confirm your new password.",
        variant: "destructive",
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: "Validation Error",
        description: "Passwords do not match.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const payload: Record<string, any> = {
        new_password: password.trim(),
        confirm_password: confirmPassword.trim(),
        logout_all_sessions: 1,
      };

      if (resetKey) {
        payload.key = resetKey; // only add when coming from reset link
      }

      const response = await fetch(getApiEndpoint("UPDATE_USER_PASSWORD"), {        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok || data.exc || data.error || data.message?.status === "failed" || data.message?.status === "error") {
        const errorMessage = data.message?.message || data.message || "Unable to reset your password. Please try again.";
        // Check if it's an expired link error
        if (typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('expired')) {
          throw new Error("This password reset link has expired. Please go back to the login page and request a new reset link.");
        }
        throw new Error(errorMessage);
      }

      toast({
        title: "Password Updated",
        description:
          resetKey
            ? "Your password has been reset successfully. Please login again."
            : "Password updated successfully.",
      });

      navigate("/"); // Redirect to login page
    } catch (err: any) {
      logger.error("Reset password error:", err);
      toast({
        title: "Reset Failed",
        description:
          err.message || "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const strength = passwordStrength(password);

  return (
    <div className="dark min-h-screen flex flex-col bg-background text-foreground">
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1.05fr_1fr]">
        {/* LEFT - secure intelligence panel */}
        <section className="relative overflow-hidden hidden lg:flex flex-col items-center justify-center p-12 lg:p-16">
          {/* radial gradient overlays */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse at 20% 0%, hsl(var(--primary) / 0.10), transparent 55%), radial-gradient(ellipse at 80% 100%, hsl(var(--primary) / 0.08), transparent 50%)',
            }}
            aria-hidden="true"
          />

          <div className="relative z-10 max-w-[640px] text-center">
            <Shield className="mx-auto mb-8 h-16 w-16 text-primary/80" strokeWidth={1.5} />
            <h1 className="text-4xl lg:text-[44px] leading-[1.08] tracking-tight font-semibold mb-6">
              Secure Intelligence
            </h1>
            <p className="text-[17px] leading-relaxed text-muted-foreground mb-12">
              Update your security credentials to continue accessing the{' '}
              {brandName} knowledge ecosystem.
            </p>

            {/* feature cards */}
            <div className="grid grid-cols-2 gap-5 text-left">
              {FEATURE_CARDS.map((card) => (
                <div
                  key={card.title}
                  className="rounded-xl border border-border bg-card/40 p-6"
                >
                  <card.icon className="h-6 w-6 text-primary mb-4" />
                  <div className="text-[15px] font-semibold text-primary">
                    {card.title}
                  </div>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    {card.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* RIGHT - reset form panel */}
        <section className="relative flex flex-col items-center justify-center p-8 sm:p-12 lg:p-14 lg:border-l border-border">
          <div className="w-full max-w-md rounded-xl border border-border bg-card/40 p-8 sm:p-10">
            {/* brand */}
            <span className="inline-flex items-center mb-8">
              {/* Fade lives on the <img>, not this wrapper: an ancestor at
                  fractional opacity creates a stacking context that traps the
                  blend, flashing the black box mid-fade. */}
              <img
                src={brandLogo || site_logo}
                alt={brandName}
                className="h-14 w-auto"
                style={{
                  filter: 'invert(1)',
                  mixBlendMode: 'screen',
                  opacity: logoLoaded ? 1 : 0,
                  transition: 'opacity 200ms ease',
                }}
                onLoad={() => setLogoLoaded(true)}
                onError={(e) => {
                  // If the company-uploaded logo URL is broken, fall back to bundled default.
                  const img = e.currentTarget as HTMLImageElement;
                  if (img.src !== site_logo && !img.src.endsWith(site_logo)) {
                    img.src = site_logo;
                  } else {
                    img.style.display = 'none';
                  }
                }}
              />
            </span>

            <h2 className="text-[28px] leading-tight tracking-tight font-semibold mb-2">
              Reset Password
            </h2>
            <p className="text-[15px] text-muted-foreground mb-8">
              Please choose a new, secure password for your account.
            </p>

            <form onSubmit={handleReset} className="space-y-6">
              {/* New Password */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">New Password</Label>
                  <span className={`text-sm font-medium ${strength.className}`}>
                    {strength.label}
                  </span>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter new password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pr-10 relative h-11"
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {/* strength bar */}
                <div className="h-1 w-full rounded-full bg-secondary overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${strength.fill}`} />
                </div>
                <p className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
                  <Info className="h-3.5 w-3.5" />
                  Minimum 8 characters with numbers and symbols.
                </p>
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pr-10 relative h-11"
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button type="submit" className="w-full h-12" disabled={loading}>
                {loading ? (
                  "Resetting..."
                ) : (
                  <>
                    <Lock className="mr-2 h-4 w-4" />
                    Reset Password
                  </>
                )}
              </Button>
            </form>

            {/* Back to login */}
            <div className="mt-8 pt-6 border-t border-border text-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-foreground/90 hover:text-primary transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Login
              </Link>
            </div>
          </div>
        </section>
      </div>

      <SiteFooter />
    </div>
  );
};

export default ResetPassword;
